import React, { useState, useEffect } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';

const CONTROLLER = 'eventsController.php';

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'title', label: 'Title' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'endTime', label: 'End Time', render: (val) => val || '-' },
  { key: 'location', label: 'Location' },
  { key: 'capacity', label: 'Capacity', render: (val) => (val !== null && val !== undefined && val !== '' ? val : 'Unlimited') },
  { 
    key: 'paymentLink', 
    label: 'Payment Link',
    render: (val) => val ? (
      <a href={val} target="_blank" rel="noopener noreferrer">
        {val}
      </a>
    ) : '-'
  },
  { 
    key: 'active', 
    label: 'Active', 
    render: (val) => (val ? 'Yes' : 'No') 
  },
];

const initialFormState = {
  title: '',
  startTime: '',
  endTime: '',
  location: '',
  description: '',
  memberPrice: '',
  nonMemberPrice: '',
  capacity: '',
  paymentLink: '',
  active: true,
};

export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setEvents(res.events || []);
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Prepare payload matching backend eventsDataHandler expectations
    const payload = {
      ...form,
      capacity: form.capacity !== '' ? parseInt(form.capacity, 10) : null,
      memberPrice: form.memberPrice !== '' ? parseFloat(form.memberPrice) : 0.00,
      nonMemberPrice: form.nonMemberPrice !== '' ? parseFloat(form.nonMemberPrice) : 0.00,
    };

    try {
      if (editingId) {
        await client.put(CONTROLLER, editingId, payload);
        setSuccess('Event updated successfully.');
      } else {
        await client.post(CONTROLLER, payload);
        setSuccess('Event created successfully.');
      }

      setForm(initialFormState);
      setEditingId(null);
      await loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (eventItem) => {
    setEditingId(eventItem.id);
    setForm({
      title: eventItem.title || '',
      startTime: eventItem.startTime || '',
      endTime: eventItem.endTime || '',
      location: eventItem.location || '',
      description: eventItem.description || '',
      memberPrice: eventItem.memberPrice !== undefined ? eventItem.memberPrice : '',
      nonMemberPrice: eventItem.nonMemberPrice !== undefined ? eventItem.nonMemberPrice : '',
      capacity: eventItem.capacity !== null && eventItem.capacity !== undefined ? eventItem.capacity : '',
      paymentLink: eventItem.paymentLink || eventItem.payment_link || '',
      active: eventItem.active !== undefined ? Boolean(eventItem.active) : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      setError('');
      await client.delete(CONTROLLER, id);
      setSuccess('Event deleted successfully.');
      await loadEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Event Management</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <form onSubmit={handleSubmit} className="admin-form-container grid-2-col">
        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            placeholder="e.g. Networking Evening"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            placeholder="e.g. Lausanne"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Start Time *</label>
          <input
            type="datetime-local"
            required
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>End Time</label>
          <input
            type="datetime-local"
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Member Price (CHF)</label>
          <input
            type="number"
            step="0.01"
            placeholder="e.g. 0.00"
            value={form.memberPrice}
            onChange={(e) => setForm({ ...form, memberPrice: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Non-Member Price (CHF)</label>
          <input
            type="number"
            step="0.01"
            placeholder="e.g. 50.00"
            value={form.nonMemberPrice}
            onChange={(e) => setForm({ ...form, nonMemberPrice: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Total Capacity / Seats</label>
          <input
            type="number"
            min="0"
            placeholder="e.g. 80"
            value={form.capacity}
            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Payment Link</label>
          <input
            type="url"
            placeholder="https://buy.stripe.com/..."
            value={form.paymentLink}
            onChange={(e) => setForm({ ...form, paymentLink: e.target.value })}
          />
        </div>

        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
          <label style={{ margin: 0, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              style={{ marginRight: '8px' }}
            />
            Active Event
          </label>
        </div>

        <div className="form-group" style={{ gridColumn: 'span 2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label>Description</label>
            <span style={{ fontSize: '12px', color: '#888' }}>
              {form.description.length}/1000
            </span>
          </div>
          <textarea
            rows="4"
            maxLength={1000}
            placeholder="Event summary and agenda (max 1000 characters)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="btn-actions-container" style={{ gridColumn: 'span 2', justifyContent: 'flex-end' }}>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(initialFormState);
              }}
              className="btn btn-cancel"
            >
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-submit-professionals">
            {editingId ? 'Update Event' : 'Add Event'}
          </button>
        </div>
      </form>

      <div className="admin-table-section">
        <DataTable columns={columns} data={events} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
}